"use client";

import type { NavLink } from "@/components/landing/types/nav";
import { useEffect, useState } from "react";

/** Tracks page scroll offset and which nav section is currently in view. */
export function useActiveSection(nav: NavLink[]) {
	const [scrolled, setScrolled] = useState(false);
	const [active, setActive] = useState<string | null>(nav[0]?.href ?? null);

	useEffect(() => {
		function onScroll() {
			setScrolled(window.scrollY > 12);
		}
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => {
		const sections = nav
			.map((n) => document.getElementById(n.href.replace("#", "")))
			.filter((el): el is HTMLElement => Boolean(el));
		if (sections.length === 0) return;
		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((entry) => entry.isIntersecting)
					.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
				if (visible?.target.id) setActive(`#${visible.target.id}`);
			},
			{ rootMargin: "-30% 0px -55% 0px", threshold: [0.1, 0.25, 0.5] },
		);
		sections.forEach((section) => observer.observe(section));
		return () => observer.disconnect();
	}, [nav]);

	return { scrolled, active };
}
